import React, { useRef, useState } from 'react';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';

import { userService } from '@paragon/data-connections';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { logInWithCredentials } from '../../../actions/authenticationActions';
import { getClientUrls } from '../../../selectors/clientUrls';
import { LoginDataSliceState } from '../../../store/logInDataSlice';
import { ErrorForm } from '../components/ErrorForm';
import { LoginForm } from '../components/LoginForm';
import { LoginInput } from '../components/LoginInput';
import { LoginTypography } from '../components/LoginTypography';
import { useLoginFlash } from '../LoginScreen';

type ResetStage = 'enterVerificationCode' | 'choosePassword' | 'chooseTrial' | 'error';

interface Props {
  readonly email: string;
  readonly onAccountCreated?: () => void;
  readonly setCancelDisabled?: (disabled: boolean) => void;
}

export function VerifyAccount({ email, onAccountCreated, setCancelDisabled }: Props) {
  const dispatch = useDispatch<ThunkDispatch<LoginDataSliceState, void, Action>>();
  const flash = useLoginFlash();

  const { designUrl } = useSelector(getClientUrls);

  const [stage, _setStage] = useState<ResetStage>('enterVerificationCode');
  const setStage = (stage: ResetStage) => {
    flash.clear();
    _setStage(stage);
  };

  const [verificationCode, setVerificationCode] = useState('');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [checkingPasswords, setCheckingPasswords] = useState(false);

  const [startingTrial, setStartingTrial] = useState(false);

  const passwordField = useRef<HTMLInputElement | null>(null);

  switch (stage) {
    case 'enterVerificationCode': {
      return (
        <LoginForm
          submitText="Verify"
          onSubmit={async () => {
            const response = await userService.signup.verify(email, verificationCode.trim());
            if (response.verified) {
              setStage('choosePassword');
            } else {
              flash.set('error', response.message ?? 'Not verified');
            }
          }}
          disabled={verificationCode.trim().length === 0}
        >
          <LoginTypography>
            We sent a verification code to your email address. Please enter it below to proceed.
          </LoginTypography>
          <LoginInput
            autoFocus
            disableAutoComplete
            label="Verification Code"
            value={verificationCode}
            onChange={setVerificationCode}
          />
        </LoginForm>
      );
    }

    case 'choosePassword': {
      return (
        <LoginForm
          submitText="Set Password and Create Account"
          onSubmit={async () => {
            // If there was an invalid submission attempt before, we don't want the flash message hanging around while
            // the spinner is going.
            flash.clear();

            if (password !== passwordConfirm) {
              setCheckingPasswords(true);
              flash.set('warning', 'Passwords do not match.');
              return;
            }

            if (password === 'paragon') {
              flash.set('warning', 'That password is not allowed.  Please choose a different password.');

              setPassword('');
              setPasswordConfirm('');

              passwordField.current?.focus();

              return;
            }

            try {
              if (setCancelDisabled) setCancelDisabled(true);
              await userService.signup.finish(email, verificationCode.trim(), password);

              onAccountCreated?.();
              const createdUserOrNull = await dispatch(logInWithCredentials(email, password));

              if (createdUserOrNull) setStage('chooseTrial');
              else flash.set('error', 'Failed to log in after account creation.');
            } catch (error) {
              console.error(error);
              if (setCancelDisabled) setCancelDisabled(false);
              setStage('error');
              return;
            }
          }}
          spinnerText={'Creating your account'}
          disabled={password.trim().length === 0}
        >
          <LoginTypography>Please choose a password.</LoginTypography>
          <LoginInput
            autoFocus
            password
            label="Password"
            value={password}
            error={checkingPasswords && password !== passwordConfirm}
            inputRef={passwordField}
            onChange={setPassword}
          />
          <LoginInput
            password
            label="Confirm Password"
            value={passwordConfirm}
            error={checkingPasswords && password !== passwordConfirm}
            onChange={setPasswordConfirm}
          />
        </LoginForm>
      );
    }

    case 'chooseTrial': {
      const openDesignClient = () => {
        window.location.href = `${designUrl}?welcome=1`;
      };

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 1 }}>
          <LoginTypography>Your account is ready!</LoginTypography>

          <Button
            fullWidth
            variant="contained"
            loading={startingTrial}
            sx={{ mt: 4 }}
            onClick={async () => {
              flash.clear();
              setStartingTrial(true);

              try {
                await userService.users.startFreeTrial();
              } catch (error) {
                console.error(error);
                setStartingTrial(false);
                flash.set(
                  'error',
                  'We could not start your trial. You can continue with Free and start it later from your account menu.',
                );
                return;
              }

              openDesignClient();
            }}
          >
            Start My 30-Day Trial
          </Button>
          <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>
            <strong>No credit card required.</strong> Get full access to design, layout, and downloads.
          </Typography>

          <Button fullWidth variant="outlined" disabled={startingTrial} sx={{ mt: 4 }} onClick={openDesignClient}>
            Continue with Free
          </Button>
          <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>
            Explore Paragon with limited access. Your 30-day trial stays available &mdash; start it any time from your
            account menu.
          </Typography>
        </Box>
      );
    }

    case 'error':
      return <ErrorForm />;

    default:
      assertExhaustiveSwitch(stage, 'Unknown verify account stage.');
  }
}
